import * as React from "react";
import {lazyInject} from "@athena-portal/core";
import {IWithRouterProps, Overlay, withRouter} from "@athena-portal/components";
import {
    Types,
    IFalconPropositionEngine,
    productHelper,
    ProductsEnum,
} from "@falcon/services";
import {RetrieveQuoteFailureMessagePage} from "./RetrieveQuoteFailureMessagePage";
import {PluginTemplates} from "@falcon/plugin-falcon-journey";
import * as moment from "moment";
import {getLocalStorage, isBeforeRebranding} from "../../CommonFunctions";
import {Navigate} from "react-router-dom";

interface IState {
    interaction?: any;
    journey?: any;
    loadingInteraction?: boolean;
    loadingJourney?: boolean;
    doRedirectToAllianzOnlinePage: boolean; // NOSONAR
}

interface IParams {
    product?: any;
    process?: any;
    interactionId?: any;
    languageCode?: any;
    email?: any;
    dateOfBirth: any;
    [key: string]: string | undefined;
}

export class RetrieveQuotePagePlain extends React.Component<IWithRouterProps<IParams>, IState> {

    @lazyInject(Types.FalconPropositionEngine)
    private propositionEngine: IFalconPropositionEngine;

    private pluginTemplates = new PluginTemplates();

    constructor(props: IWithRouterProps<IParams>) {
        super(props);
        this.state = {
            doRedirectToAllianzOnlinePage: false,
        };
    }

    public componentDidMount() {
        this.pluginTemplates.setPageTitle("Quote retrieve");

        this.setState({loadingInteraction: true, loadingJourney: true});

        const product = this.props.params.product;
        const process = this.props.params.process;
        const interactionId = this.props.params.interactionId;
        const languageCode = this.props.params.languageCode;
        const email = this.props.params.email;
        const dateOfBirth = this.props.params.dateOfBirth;

        const decodedProduct: any = productHelper.decodeShortenedProduct(product);

        this.propositionEngine.fetchInteractionByEmailAndDob(decodedProduct, interactionId, email, dateOfBirth).subscribe(
            (interaction: any) => {
                this.setState({interaction, loadingInteraction: false});
                this.propositionEngine.fetchJourneyByInteractionEmailAndDob(decodedProduct, process, interactionId, languageCode, email, dateOfBirth).subscribe(
                    (journey: any) => {
                        this.setState({journey, loadingJourney: false})
                    });

                if (window.location.pathname.includes("/retrieveQuote") && interaction) {
                    this.setRedirectToAllianzOnlinePage(interaction);
                }
            });
    }

    public render() {
        if (this.state.loadingInteraction || this.state.loadingJourney) {
            return <Overlay show={true}/>
        }

        const {
            interaction,
            journey
        } = this.state;
        if (interaction && interaction.headerInfo && interaction.status && journey) {
            if (moment().isAfter(interaction.headerInfo.expiryDate) || moment().subtract(1, "days").isAfter(interaction.headerInfo.startDate)
                || interaction.status === "AGREEMENT") {
                const newQuoteUrl: string = "/journey/init/product/" + productHelper.decodeShortenedProduct(interaction.product) + "/process/nb";

                return (
                    <RetrieveQuoteFailureMessagePage message={
                        <span>Your quote has expired.
                            <a href={newQuoteUrl} id="newQuote"> Start quote again</a>
                        </span>}/>
                );
            } else if (interaction.product === ProductsEnum.MOTOR.valueOf()) {
                return this.processMotorSubscriptionQuote(interaction);
            } else if (interaction.product === ProductsEnum.WOLF_MOTOR.valueOf()
                || interaction.product === ProductsEnum.HOME.valueOf()) {
                return this.processAnnualQuote(interaction);
            } else {
                return (
                    <Navigate to={"/journey/show/product/RentersInsurance/process/nb/quote/" + interaction._id}/>
                );
            }
        } else {
            return (
                <RetrieveQuoteFailureMessagePage message="Quote not found, please enter details again..."/>
            );
        }
    }

    private processMotorSubscriptionQuote(interaction: any) {
        if (interaction.status === "BINDING_QUOTE") {
            if (this.doRedirectToAllianzOnlinePage()) {
                return ( <Navigate to={`/allianzOnline?retrieveQuote=true&product=MotorInsurance&page=summary&interactionId=${interaction._id}`} /> );
            } else {
                return (
                    <Navigate to={"/journey/show/product/MotorInsurance/process/nb/summary/" + interaction._id}/>
                );
            }
        } else if (interaction.status === "TEMP_INTERACTION" && interaction.source) {
            return (
                <Navigate to={"/journey/show/product/MotorInsurance/process/nb/gdprPage/" + interaction._id}/>
            );
        } else {
            return (
                <RetrieveQuoteFailureMessagePage message="Incorrect quote, please enter details again..."/>
            );
        }
    }

    private processAnnualQuote(interaction: any) {
        // For CTM aggregator we should be able to retrieve only updated quote (that was cloned from CTM original quote)
        if (interaction.status === "BINDING_QUOTE" && (!interaction.source || (interaction.source === "ctm" && interaction.clonedFrom))) {
            const page = interaction.product === ProductsEnum.WOLF_MOTOR.valueOf() ? "summary" : "quote";

            if (this.doRedirectToAllianzOnlinePage()) {
                return ( <Navigate to={`/allianzOnline?retrieveQuote=true&product=${interaction.product}&page=${page}&interactionId=${interaction._id}`} />);
            } else {
                return (
                    <Navigate
                        to={"/journey/show/product/" + interaction.product + "/process/nb/" + page + "/" + interaction._id}/>
                )
            }
        } else {
            const aggErrorMessage = "Sorry, we can't retrieve your quote. This quote can only be retrieved from ";
            let quoteFailureMessage;

            switch (interaction.source) {
                case "ctm":
                case "ctmhome":
                    quoteFailureMessage = <>{aggErrorMessage} <a
                        href="https://comparethemarket.com">Comparethemarket.com</a></>;
                    break;
                case "cnf":
                case "cnfhome":
                    quoteFailureMessage = <>{aggErrorMessage} <a href="https://confused.com">Confused.com</a></>;
                    break;
                case "gc":
                case "gchome":
                    quoteFailureMessage = <>{aggErrorMessage} <a href="https://www.gocompare.com">Gocompare.com</a></>;
                    break;
                case "msm":
                case "msmhome":
                    quoteFailureMessage = <>{aggErrorMessage} <a
                        href="https://www.moneysupermarket.com/">MoneySuperMarket.com</a></>;
                    break;
                case "elx":
                    quoteFailureMessage = <>{aggErrorMessage} <a href="https://www.lvelectrix.co.uk/">ElectriX</a></>;
                    break;
                default:
                    quoteFailureMessage = "Quote not found, please enter details again...";
                    break;

            }

            return <RetrieveQuoteFailureMessagePage message={quoteFailureMessage}/>;
        }
    }

    private async setRedirectToAllianzOnlinePage(interaction: any) {
        this.setState({
            doRedirectToAllianzOnlinePage: this.hasQuotesBeforeRebranding(interaction)
        });
    }

    private readonly doRedirectToAllianzOnlinePage = (): boolean => {
        if (this.state.doRedirectToAllianzOnlinePage) {
            this.setState({doRedirectToAllianzOnlinePage: false});
            return true;
        }

        return false;
    }

    private hasQuotesBeforeRebranding(interaction: any): boolean {
        const isShown = getLocalStorage("allianzOnlineShown" + interaction?._id);

        return isBeforeRebranding(interaction.createdOn) && !isShown;
    }
}

export const RetrieveQuotePage = withRouter<{}, IParams>(RetrieveQuotePagePlain)
